import { Blac, Cubit } from "blac-next";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { userState } from "src/state/state";

interface AppDebugState {
  extra: Record<string, unknown>;
  show: boolean;
  minified: boolean;
}

// Custom event dispatched on `window` to control the debug panel from the
// console (or any code that can reach `window`). Example:
//   window.dispatchEvent(new CustomEvent("9am-debug-action", {
//     detail: { action: "minify" }
//   }));
// Or use the `window.debug9am` shorthand registered below.
export const DEBUG_ACTION_EVENT = "9am-debug-action";

export type DebugAction =
  | "open"
  | "close"
  | "toggle"
  | "minify"
  | "expand"
  | "toggle-minify";

declare global {
  interface Window {
    debug9am?: {
      open: () => void;
      close: () => void;
      toggle: () => void;
      minify: () => void;
      expand: () => void;
      toggleMinify: () => void;
    };
  }
}

export default class AppDebugCubit extends Cubit<AppDebugState> {
  private isOpenSessStorageKey = "debug-bloc-is-open";
  isMinifiedSessStorageKey = "debug-bloc-is-minified";

  private triggerWord = "debug9am";
  private lastKeys = "";
  private countMovePastMiddle = 0;
  private middle = window.innerWidth / 2;
  private aboveMiddle = false;

  constructor() {
    super({
      extra: {},
      show: false,
      minified: sessionStorage.getItem("debug-bloc-is-minified") === "true"
    });
  }

  init = () => {
    const isOpen = sessionStorage.getItem(this.isOpenSessStorageKey);
    if (isOpen === "true") {
      this.setShow(true);
    }
    this.installConsoleHelper();
  };

  private typeListener = (e: KeyboardEvent) => {
    this.lastKeys = this.lastKeys + e.key;
    this.lastKeys = this.lastKeys.slice(-this.triggerWord.length);
    if (this.lastKeys === this.triggerWord) {
      this.toggleOrReset();
    }
  };

  private touchListener = (e: TouchEvent) => {
    if (e.touches.length > 1) {
      return;
    }
    const [touch] = e.touches;
    if (touch.clientX > this.middle) {
      if (!this.aboveMiddle) {
        this.countMovePastMiddle++;
        if (this.countMovePastMiddle > 5) {
          this.setShow(true);
        }
      }
      this.aboveMiddle = true;
    } else {
      this.aboveMiddle = false;
    }
  };

  private touchEndListener = () => {
    this.countMovePastMiddle = 0;
  };

  setShow = (show: boolean): void => {
    this.emit({ ...this.state, show });
    sessionStorage.setItem(this.isOpenSessStorageKey, String(show));
  };

  setMinified = (minified: boolean): void => {
    this.emit({ ...this.state, minified });
    sessionStorage.setItem(this.isMinifiedSessStorageKey, String(minified));
  };

  toggleMinified = (): void => {
    this.setMinified(!this.state.minified);
  };

  toggleOrReset = (): void => {
    if (this.state.show) {
      this.setShow(false);
      setTimeout(() => this.setShow(true), 50);
    } else {
      this.setShow(true);
    }
  };

  /** Closes the debug panel AND disables any debug-only simulations (e.g. Transcarent session). */
  closePanel = (): void => {
    this.setShow(false);
    userState.simulateTranscarentSession(false);
    Blac.getBloc(AppViewCubit).disablePartnerSession();
  };

  private handleAction = (action: DebugAction): void => {
    switch (action) {
      case "open":
        this.setShow(true);
        this.setMinified(false);
        break;
      case "close":
        this.closePanel();
        break;
      case "toggle":
        this.setShow(!this.state.show);
        break;
      case "minify":
        this.setMinified(true);
        break;
      case "expand":
        this.setShow(true);
        this.setMinified(false);
        break;
      case "toggle-minify":
        this.toggleMinified();
        break;
    }
  };

  private debugActionListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ action?: DebugAction }>).detail;
    if (detail?.action) {
      this.handleAction(detail.action);
    }
  };

  private installConsoleHelper = (): void => {
    if (window.debug9am) {
      return;
    }
    const fire = (action: DebugAction): void => {
      window.dispatchEvent(
        new CustomEvent(DEBUG_ACTION_EVENT, { detail: { action } })
      );
    };
    window.debug9am = {
      open: () => fire("open"),
      close: () => fire("close"),
      toggle: () => fire("toggle"),
      minify: () => fire("minify"),
      expand: () => fire("expand"),
      toggleMinify: () => fire("toggle-minify")
    };
  };

  addCustomListeners = () => {
    document.addEventListener("keydown", this.typeListener, { passive: true });
    window.addEventListener("touchmove", this.touchListener, { passive: true });
    window.addEventListener("touchend", this.touchEndListener, {
      passive: true
    });
    window.addEventListener(DEBUG_ACTION_EVENT, this.debugActionListener);
  };

  removeCustomListeners = () => {
    document.removeEventListener("keydown", this.typeListener);
    window.removeEventListener("touchmove", this.touchListener);
    window.removeEventListener("touchend", this.touchEndListener);
    window.removeEventListener(DEBUG_ACTION_EVENT, this.debugActionListener);
  };

  setExtra = (extra: Record<string, unknown>): void => {
    this.emit({ ...this.state, extra });
  };
}
